/**
 * „Fertig“ — die Antwort ist abgeschlossen. (17.10.2026, Martin 09:45)
 *
 * Martin: Bisher kann er die laufende Ausgabe durch einen Tipp auf die Wiedergabe-Pille unterbrechen, und dann öffnet sich
 * sein Mikro (Ticket 2695, lib/unterbrechen.ts). Er will zusätzlich ein einfaches „Fertig“: die Antwort als abgeschlossen
 * empfinden und den ganzen Vorlese-Prozess stoppen — OHNE dass das Mikro aufgeht und ohne dass vom Rest noch etwas
 * gesprochen wird.
 *
 * Der Knopf sitzt IN der Wiedergabe-Pille (lib/vorlese-pille.ts), neben „Unterbrechen – ich spreche“. Die Sprechtaste und
 * der Tipp auf die Pille behalten „unterbrechen + Mikro öffnen“.
 *
 * Was „Fertig“ tut (`fertigDruecken`, die Reihenfolge ist Absicht):
 *   1. merken: dieser Zug ist abgeschlossen (`merkeFertig`). Danach
 *        - bleibt das Mikro nach der Antwort zu, in jeder Stufe (shared/mikro-nach-antwort.ts, Grund `fertig-gedrueckt`);
 *        - beginnt in diesem Zug keine neue automatische Stimme Vesperas (`fertigHaeltStimme`) — ein Werkzeug-Hinweis,
 *          ein später Nachschub; nach `done` noch `FERTIG_NACHLAUF_MS` lang (Rückfall „done ohne Text“, frische Blasen
 *          über den Kanal — chat-http.ts beendet den Zug schon VOR onDone);
 *        - wird ein Beitrag des Schmieds, der bis `FERTIG_NACHLAUF_MS` danach im selben Zug kommt, nur angezeigt
 *          (`fertigHaeltSchmied`).
 *   2. alles stoppen, was klingt oder in der Schlange wartet (app.ts `stopCurrentTts("fertig")`: jede Schlange der Seite,
 *      damit auch noch nicht gestartete Vertonungen; die Pille verschwindet);
 *   3. die schon vertonten, noch nicht gesprochenen Beiträge des Schmieds verwerfen (sie stehen in keiner Schlange der
 *      Seite, solange sie auf Stille warten);
 *   4. das Mikro, das auf den Schmied wartet, ohne Öffnen beenden (lib/schmied-warten.ts `fertig`);
 *   5. über die Seiten: geparkte und wartende Stimmen verwerfen, andere Seiten und Geräte erfahren es
 *      (lib/seiten-abgleich.ts `stimmenFertig`).
 * Kein Schritt startet eine Aufnahme — es gibt in diesem Ablauf keinen Rückruf dafür.
 */

/*
 * Der Merker selbst gehört zum Zug (lib/zug-ende.ts `merkeFertig`): ein neuer Zug beginnt ohne, und jedes Vorlese-Ende trägt
 * ihn mit (`zugEndeStand().fertig`) — so entscheidet der Mikro-Hörer in app.ts ohne eigenen Zugriff hierher.
 */
import { fertigImZug, fertigSeitMs, merkeFertig } from "./zug-ende.js";

/**
 * So lange nach „Fertig“ bleibt ein neuer Beitrag des Schmieds im selben Zug still (nur Blase) — und eine neue automatische
 * Stimme Vesperas nach dem Ende des Zuges (`fertigHaeltStimme`). Bewusst kurz (15 s, vorher 90 s):
 * 90 s Stille nach einem kurzen „Fertig“ wirkten wie „die Stimme ist weg“ (Martin 17.10.). Was danach kommt, ist neue
 * Arbeit, die Martin hören soll. Eine NEUE Aufnahme (lib/ptt.ts `starteAufnahme` → `loescheFertig`) und ein NEUER Zug (Tippen, Senden, Anruf:
 * `zugBeginnt` in chat-http.ts und ptt.ts) beenden „Fertig“ sofort — auch innerhalb dieser Spanne.
 */
export const FERTIG_NACHLAUF_MS = 15_000;

/** Wurde in diesem Zug „Fertig“ gedrückt? (Mikro nach der Antwort, spätes Öffnen nach dem Schmied) */
export function fertigFuerZug(zug: number): boolean {
  return fertigImZug(zug);
}

/**
 * Bleibt eine NEUE automatische Stimme Vesperas still? Nur im selben Zug und nie bei einem Knopf oder Tipp (der Abspielknopf
 * ist ein neuer, ausdrücklicher Wunsch). Solange der Zug läuft immer; nach seinem Ende (`done`) noch `FERTIG_NACHLAUF_MS`
 * ab „Fertig“ — Prüfauflage 17.10.: chat-http.ts beendet den Zug vor onDone, der Rückfall „done ohne Text“ und frische
 * Blasen über den Kanal sprächen sonst nach „Fertig“ doch noch. Ein neuer Zug beginnt ohne (lib/zug-ende.ts).
 */
export function fertigHaeltStimme(a: { zug: number; zugLaeuft: boolean; knopf: boolean; jetzt?: number }): boolean {
  if (a.knopf || !fertigImZug(a.zug)) return false;
  return a.zugLaeuft || fertigSeitMs(a.jetzt ?? Date.now()) < FERTIG_NACHLAUF_MS;
}

/** Bleibt ein Beitrag des Schmieds, der JETZT kommt, still (nur Blase)? */
export function fertigHaeltSchmied(zug: number, jetzt: number = Date.now()): boolean {
  return fertigImZug(zug) && fertigSeitMs(jetzt) < FERTIG_NACHLAUF_MS;
}

/** Wer „Fertig“ auslöste: Tipp, Tastatur, oder eine andere Seite / ein anderes Gerät. */
export type FertigKanal = "pille-fertig" | "pille-fertig-taste" | "anderswo";

/**
 * 20.10.2026 (Martin 23:05): spricht gerade eine VON HAND gestartete Wiedergabe (Abspielknopf einer Blase, Mini-Player) und tippt
 * Martin hier „Fertig“, beendet das nur diese Wiedergabe. Beiträge, die in der Vorlese-Reihe dahinter warten (Zimmermanns Frage),
 * sind nicht „die Antwort“ — sie werden weiter vorgelesen und öffnen danach wie immer das Mikro. Ein „Fertig“ von einem anderen
 * Gerät (`anderswo`) schließt wie bisher ab. Journal 23:03:47 `wiedergabe(schmied-knopf)`, danach keine `wiedergabe(zimmermann)`.
 */
export function fertigNurFuerKnopf(kanal: FertigKanal, knopfSpricht: boolean, zugLaeuft = false): boolean {
  // Prüfer-Auflage 3: läuft Vesperas Zug noch, schließt „Fertig“ wie bisher den ganzen Zug ab — sonst spräche eine spätere
  // automatische Stimme dieses Zuges doch noch (`fertigHaeltStimme` braucht den Merker).
  return kanal !== "anderswo" && knopfSpricht && !zugLaeuft;
}

/** Zustände des Abspielknopfs, in denen er wirklich lädt, spricht oder pausiert (lib/vorlese-quelle.ts). */
const KNOPF_AKTIV = new Set(["loading", "playing", "paused", "wartet"]);

/**
 * Spricht gerade eine Knopf-Wiedergabe? (Prüfer-Auflage 2) Nicht allein `currentAudioBtn !== null`: ein Knopf, dessen Wiedergabe
 * schon zu Ende ist, kann kurz stehen bleiben. Nur ein Knopf in einem aktiven Zustand zählt.
 */
export function knopfWiedergabeLaeuft(knopf: { dataset: { state?: string } } | null): boolean {
  return knopf !== null && KNOPF_AKTIV.has(knopf.dataset.state ?? "idle");
}

export interface FertigAbhaengig {
  kanal: FertigKanal;
  /** `fertigNurFuerKnopf`: nur die Knopf-Wiedergabe stoppen — kein Merker, nichts verwerfen, kein Warten beenden, keine anderen Seiten. */
  nurKnopf?: boolean;
  /** Der laufende Zug (lib/zug-ende.ts `zugKennung`). */
  zug: number;
  jetzt: () => number;
  /** Alles stoppen, was auf dieser Seite klingt oder in einer Schlange wartet (app.ts `stopCurrentTts`). */
  stoppe: (grund: string) => void;
  /** Vertonte, noch nicht gesprochene Beiträge des Schmieds verwerfen; Rückgabe: wie viele. */
  verwirfSchmied: (grund: string) => number;
  /** Das Warten des Mikros auf den Schmied ohne Öffnen beenden; `true`, wenn etwas wartete. */
  schmiedWartenFertig: () => boolean;
  /** Über die Seiten (lib/seiten-abgleich.ts `stimmenFertig`); `weiter`: melden. Rückgabe: geparkt/wartend verworfen. */
  seitenFertig: (weiter: boolean, grund: string) => number;
  melde: (zeile: string) => void;
}

/** Der Ablauf von „Fertig“ (oben). Gibt die Journalzeile zurück (nur Wörter und Zahlen). */
export function fertigDruecken(d: FertigAbhaengig): string {
  const anderswo = d.kanal === "anderswo";
  const grund = anderswo ? "fertig-anderswo" : "fertig";
  if (d.nurKnopf && !anderswo) {
    d.stoppe(grund);
    const zeile = `[fertig] via=${d.kanal} zug=${d.zug} nur-knopf=1`;
    d.melde(zeile);
    return zeile;
  }
  merkeFertig(d.zug, d.jetzt());
  d.stoppe(grund);
  const schmied = d.verwirfSchmied(grund);
  const wartete = d.schmiedWartenFertig();
  const seiten = d.seitenFertig(!anderswo, grund);
  const zeile = `[fertig] via=${d.kanal} zug=${d.zug} schmied-verworfen=${schmied} seiten-verworfen=${seiten} mikro-wartete=${wartete ? 1 : 0}`;
  d.melde(zeile);
  return zeile;
}
